import React, { ReactNode, useCallback, useReducer } from 'react';
import { SortDirection, TimeConversions } from '../../config/constants';
import EmissionSpoolContext from './emissionSpoolsContext';
import emissionSpoolsReducer from './emissionSpoolsReducer';
import { ActiveSortType, EmissionSpoolDto, SortKey } from './emissionSpoolsType';

type Props = {
    children: ReactNode;
    initialSortKey: SortKey;
};

const APY_BUFFER_HOURS = 53;

const APY_BUFFER_TIME = APY_BUFFER_HOURS * TimeConversions.SECONDS_PER_HOUR;

const EmissionSpoolsState: React.FC<Props> = ({ children, initialSortKey }) => {
    const setSearchTerm = useCallback(
        (term: string): void =>
            dispatch({
                type: 'SET_SEARCH_TERM',
                payload: term,
            }),
        []
    );

    const setTotalInputAlloc = useCallback((alloc: { [key: string]: string }): void => {
        dispatch({
            type: 'SET_ALLOCATION',
            payload: alloc,
        });
    }, []);

    const setTotalAllocAmount = useCallback((allocAmount: number): void => {
        dispatch({
            type: 'SET_TOTAL_ALLOCATION_AMOUNT',
            payload: allocAmount,
        });
    }, []);

    const setUserHasVoted = useCallback((userHasVoted: boolean): void => {
        dispatch({
            type: 'SET_USER_HAS_VOTED',
            payload: userHasVoted,
        });
    }, []);

    const sort = useCallback(
        (a: EmissionSpoolDto, b: EmissionSpoolDto, activeSort: ActiveSortType) => {
            if (!activeSort?.key) return 0;

            const direction = activeSort.direction === SortDirection.ASC ? 1 : -1;

            const aValue = parseFloat(a[activeSort.key]!.toString() || '0');
            const bValue = parseFloat(b[activeSort.key]!.toString() || '0');

            return (aValue - bValue) * direction;
        },
        []
    );

    const handleSortClick = useCallback((key: SortKey, activeSort: ActiveSortType) => {
        if (!activeSort?.key || activeSort?.key !== key) {
            dispatch({
                type: 'SET_ACTIVE_SORT',
                payload: {
                    key,
                    direction: SortDirection.DESC,
                },
            });
            return;
        }

        dispatch({
            type: 'CHANGE_ACTIVE_SORT_DIRECTION',
        });
    }, []);

    const filterData = useCallback((data: EmissionSpoolDto, searchTerm: string) => {
        const spoolNameFilter = searchTerm
            ? data.spoolName!.toUpperCase().includes(searchTerm.toUpperCase())
            : true;

        const addressFilter = searchTerm
            ? data.address!.toUpperCase().includes(searchTerm.toUpperCase())
            : true;

        const searchResults = spoolNameFilter || addressFilter;

        return searchResults;
    }, []);

    const initialState = {
        APY_BUFFER_TIME,
        searchTerm: '',
        activeSort: {
            key: initialSortKey,
            direction: SortDirection.DESC,
        },
        totalInputAlloc: {},
        totalAllocAmount: 0,
        userHasVoted: false,
        setUserHasVoted,
        sort,
        handleSortClick,
        filterData,
        setSearchTerm,
        setTotalInputAlloc,
        setTotalAllocAmount,
    };

    const [state, dispatch] = useReducer(emissionSpoolsReducer, initialState);

    return (
        <EmissionSpoolContext.Provider
            value={{
                APY_BUFFER_TIME,
                searchTerm: state.searchTerm,
                activeSort: state.activeSort,
                totalInputAlloc: state.totalInputAlloc,
                totalAllocAmount: state.totalAllocAmount,
                userHasVoted: state.userHasVoted,
                sort,
                handleSortClick,
                setTotalInputAlloc,
                setTotalAllocAmount,
                setUserHasVoted,
                filterData,
                setSearchTerm,
            }}
        >
            {children}
        </EmissionSpoolContext.Provider>
    );
};

export default EmissionSpoolsState;
